/**
 * Live bot/trade event subscription hook.
 *
 * Opens a single shared WebSocket to the FastAPI /ws/trades endpoint and
 * fans server-pushed events out to any number of components that call
 * this hook. One connection per browser tab — multiple consumers share
 * the same socket via an in-module subscriber set.
 *
 * Server message shapes (see app/services/trade_reconciler.py):
 *   { type: 'trade_filled',        botId, symbol, tradeId, orderId,
 *                                   optionSymbol, action, fillPrice }
 *   { type: 'trade_terminal',      botId, symbol, tradeId, orderId,
 *                                   optionSymbol, alpacaStatus }
 *   { type: 'bot_state_changed',   botId, symbol, fromState, toState,
 *                                   retryAttempt, reason }
 *   { type: 'bot_needs_attention', botId, symbol, attempts, fromState,
 *                                   toState }
 *
 * Auto-reconnects with capped exponential backoff so a dropped
 * connection recovers without a page reload.
 */

import { useEffect, useRef, useState } from 'react';

export type TradeEventType =
  | 'trade_filled'
  | 'trade_terminal'
  | 'bot_state_changed'
  | 'bot_needs_attention'
  | 'account_synced'
  | 'position_mark';

/**
 * Live mark of an open position, pushed by the server on every strategy
 * manage tick (~1/min, sub-minute when a trigger is close).
 * Shape: { type:'position_mark', botId, symbol, strategy, at,
 *          unrealizedPnl, pnlPctOfCredit, credit, closeCost, contracts,
 *          targetPnl, stopPnl, ratchetArmed, holdArmed, trailArmed,
 *          trailPct, trailPeakPnl, trailFloorPnl, refLast }
 */
export interface PositionMark {
  botId: string;
  symbol: string;
  strategy?: string;
  at: string;
  unrealizedPnl: number;
  pnlPctOfCredit: number;
  credit: number;
  closeCost: number;
  contracts: number;
  targetPnl: number;
  stopPnl: number;
  ratchetArmed: boolean;
  /** IC: conditional hold armed this cycle (held cycles never trail). */
  holdArmed?: boolean;
  /** IC trail after the 50% target (server PR #130). */
  trailArmed?: boolean;
  /** IC trail state as the tick began; null until armed / when off. */
  trailPct?: number | null;
  trailPeakPnl?: number | null;
  trailFloorPnl?: number | null;
  /** Breadth bots also mark their reference symbol + triggers. */
  refLast?: number;
  dipTrigger?: number;
  adverseTrigger?: number;
}

export interface AlpacaAccountSnapshot {
  id?: string;
  cash?: number | null;
  buyingPower?: number | null;
  daytradingBuyingPower?: number | null;
  optionsBuyingPower?: number | null;
  equity?: number | null;
  lastEquity?: number | null;
  portfolioValue?: number | null;
  longMarketValue?: number | null;
  shortMarketValue?: number | null;
  multiplier?: number;
  currency?: string;
  status?: string;
}

export interface TradeEvent {
  type: TradeEventType;
  botId?: string;
  symbol?: string;
  tradeId?: string;
  orderId?: string;
  optionSymbol?: string;
  action?: string;
  fillPrice?: number | null;
  alpacaStatus?: string;
  fromState?: string;
  toState?: string;
  retryAttempt?: number;
  attempts?: number;
  reason?: string;
  account?: AlpacaAccountSnapshot | null;
  [k: string]: unknown;
}

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const WS_BASE = API_BASE.replace(/^http/, 'ws');

// ---- singleton connection, shared across every hook instance --------------

type Subscriber = (ev: TradeEvent) => void;
const subscribers = new Set<Subscriber>();
const statusSubscribers = new Set<(s: StreamStatus) => void>();

let sharedWs: WebSocket | null = null;
let reconnectTimer: number | null = null;
let reconnectAttempts = 0;
let currentStatus: StreamStatus = 'idle';

function setStatus(s: StreamStatus) {
  currentStatus = s;
  statusSubscribers.forEach((fn) => fn(s));
}

function openSharedSocket() {
  const token = localStorage.getItem('authToken');
  if (!token) {
    setStatus('error');
    return;
  }
  if (sharedWs && (sharedWs.readyState === WebSocket.OPEN || sharedWs.readyState === WebSocket.CONNECTING)) {
    return;
  }
  setStatus('connecting');
  const ws = new WebSocket(`${WS_BASE}/ws/trades?token=${encodeURIComponent(token)}`);
  sharedWs = ws;

  ws.onopen = () => {
    setStatus('open');
    reconnectAttempts = 0;
  };

  ws.onmessage = (ev) => {
    try {
      const data = JSON.parse(ev.data) as TradeEvent;
      if (!data || !data.type) return;
      subscribers.forEach((fn) => {
        try {
          fn(data);
        } catch {
          /* individual subscriber errors must not kill the bus */
        }
      });
    } catch {
      /* ignore malformed message */
    }
  };

  ws.onerror = () => {
    setStatus('error');
  };

  ws.onclose = () => {
    sharedWs = null;
    setStatus('closed');
    // Reconnect only while at least one subscriber still cares.
    if (subscribers.size === 0) return;
    const attempt = ++reconnectAttempts;
    const delay = Math.min(30_000, 500 * 2 ** Math.min(attempt, 6));
    reconnectTimer = window.setTimeout(openSharedSocket, delay);
  };
}

function closeSharedSocketIfIdle() {
  if (subscribers.size > 0) return;
  if (reconnectTimer !== null) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (sharedWs) {
    try {
      sharedWs.close();
    } catch {
      /* ignore */
    }
    sharedWs = null;
  }
  setStatus('idle');
}

// ---- public hook ----------------------------------------------------------

interface UseTradeStreamResult {
  status: StreamStatus;
}

/**
 * Subscribe to live bot/trade events for the current user.
 *
 * @param onEvent  Called with every event received. Keep this stable
 *                 (useCallback) to avoid re-subscribing on every render —
 *                 though a re-subscribe is harmless, just wasteful.
 */
export function useTradeStream(onEvent?: (ev: TradeEvent) => void): UseTradeStreamResult {
  const [status, setLocalStatus] = useState<StreamStatus>(currentStatus);

  // Keep the latest handler in a ref so we don't churn subscriptions.
  const handlerRef = useRef<Subscriber | null>(null);
  handlerRef.current = onEvent ?? null;

  useEffect(() => {
    const wrapped: Subscriber = (ev) => {
      handlerRef.current?.(ev);
    };
    subscribers.add(wrapped);
    openSharedSocket();

    const statusHandler = (s: StreamStatus) => setLocalStatus(s);
    statusSubscribers.add(statusHandler);
    // Sync initial status in case the socket was already open.
    setLocalStatus(currentStatus);

    return () => {
      subscribers.delete(wrapped);
      statusSubscribers.delete(statusHandler);
      // If nobody's listening anymore, tear the socket down so we don't
      // keep a connection open on a logged-out tab.
      closeSharedSocketIfIdle();
    };
  }, []);

  return { status };
}
